"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import TestimonialsGlow from "@/app/assets/icons/testimonials/glow";
import {
    TESTIMONIAL_STATS,
    TESTIMONIALS_NEW,
    type TestimonialItem,
} from "@/app/components/icm 3.0/testimonials-data";

const COUNT_DURATION_MS = 5000;

function parseStatValue(value: string): {
    target: number;
    decimals: number;
    suffix: string;
} {
    const match = value.match(/^([\d.]+)(.*)$/);
    if (!match) return { target: 0, decimals: 0, suffix: value };
    const numeric = match[1] ?? "0";
    const decimals = numeric.includes(".")
        ? (numeric.split(".")[1]?.length ?? 0)
        : 0;
    return {
        target: Number.parseFloat(numeric),
        decimals,
        suffix: match[2] ?? "",
    };
}

function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
}

const AnimatedStatValue = ({
    value,
    className,
}: {
    value: string;
    className?: string;
}) => {
    const { target, decimals, suffix } = parseStatValue(value);
    const [display, setDisplay] = useState(
        () => `0${decimals > 0 ? `.${"0".repeat(decimals)}` : ""}${suffix}`,
    );
    const ref = useRef<HTMLParagraphElement>(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;

        const finish = () => {
            setDisplay(`${target.toFixed(decimals)}${suffix}`);
            hasAnimated.current = true;
        };

        if (prefersReducedMotion) {
            finish();
            return;
        }

        let frame = 0;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting || hasAnimated.current) return;
                hasAnimated.current = true;
                observer.disconnect();

                const start = performance.now();

                const tick = (now: number) => {
                    const t = Math.min((now - start) / COUNT_DURATION_MS, 1);
                    const current = target * easeOutCubic(t);
                    setDisplay(`${current.toFixed(decimals)}${suffix}`);
                    if (t < 1) {
                        frame = window.requestAnimationFrame(tick);
                    }
                };

                frame = window.requestAnimationFrame(tick);
            },
            { threshold: 0.4 },
        );

        observer.observe(node);
        return () => {
            observer.disconnect();
            window.cancelAnimationFrame(frame);
        };
    }, [target, decimals, suffix]);

    return (
        <p ref={ref} className={className}>
            {display}
        </p>
    );
};

const TestimonialCard = ({ item }: { item: TestimonialItem }) => (
    <article className="flex h-[235px] w-[247px] shrink-0 flex-col justify-between rounded-2xl border border-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)] bg-white p-4 md:h-[252px] md:w-[300px] md:p-5">
        <p className="text-sm font-regular leading-5 text-[#575757] md:text-base md:leading-[22px]">
            &ldquo;{item.quote}&rdquo;
        </p>
        <div className="flex items-start gap-4">
            <div className="relative size-12 shrink-0 overflow-hidden rounded-full">
                <Image
                    src={item.avatar}
                    alt=""
                    fill
                    className="object-cover object-top"
                    sizes="48px"
                />
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-full bg-[rgba(166,163,163,0.2)]"
                />
            </div>
            <div className="flex min-w-0 flex-col gap-[5px]">
                <p className="text-base font-bold leading-[22px] text-black md:text-lg md:leading-6">
                    {item.name}
                </p>
                <p className="text-sm font-medium leading-5 text-[var(--colors-text-body-text-1,#767676)]">
                    {item.role}
                </p>
            </div>
        </div>
    </article>
);

const StatsRow = () => (
    <div className="flex items-center gap-8 whitespace-nowrap md:gap-12">
        {TESTIMONIAL_STATS.map(({ value, label }) => (
            <div key={label} className="flex flex-col items-start justify-center gap-3">
                <AnimatedStatValue
                    value={value}
                    className="text-[30px] font-bold leading-9 text-black md:text-5xl md:leading-[58px]"
                />
                <p className="text-base font-regular leading-[22px] text-[var(--colors-text-body-text-1,#767676)] md:text-xl md:leading-[26px]">
                    {label}
                </p>
            </div>
        ))}
    </div>
);

const MarqueeColumn = ({
    items,
    direction,
    offsetClass,
}: {
    items: TestimonialItem[];
    direction: "up" | "down";
    offsetClass?: string;
}) => {
    const loop = [...items, ...items];
    const anim =
        direction === "up"
            ? "animate-testimonials-marquee-up"
            : "animate-testimonials-marquee-down";

    return (
        <div className={`flex flex-col gap-5 ${offsetClass ?? ""}`}>
            <div className={`flex flex-col gap-5 ${anim} motion-reduce:animate-none`}>
                {loop.map((item, index) => (
                    <TestimonialCard key={`${item.name}-${direction}-${index}`} item={item} />
                ))}
            </div>
        </div>
    );
};

/**
 * Testimonials — Figma desktop 2745:21026 / mobile 2541:30640
 * Card copy lives in `testimonials-data.ts`.
 */
const TestimonialsNew = () => {
    const colA = TESTIMONIALS_NEW;
    const colB = [...TESTIMONIALS_NEW].reverse();

    return (
        <section
            aria-labelledby="testimonials-heading"
            className="relative w-full overflow-hidden bg-[#FEF5EE] px-6 py-10 md:px-[120px] md:pt-[120px] md:pb-8"
        >
            <div className="relative mx-auto flex w-full max-w-[1200px] flex-col gap-12 md:flex-row md:items-stretch md:gap-12">
                <div className="flex w-full flex-col gap-12 md:w-[440px] md:shrink-0 md:justify-between md:gap-0 md:py-10">
                    <header className="flex flex-col gap-3 md:gap-2">
                        <p className="text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px] md:tracking-[1.6px]">
                            TESTIMONIALS
                        </p>
                        <h2
                            id="testimonials-heading"
                            className="text-2xl font-bold leading-8 text-black md:text-5xl md:leading-none"
                        >
                            Results that speak Volume
                        </h2>
                    </header>

                    <StatsRow />
                </div>

                {/* Mobile horizontal cards */}
                <div className="relative -mx-6 md:hidden">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute left-[111px] top-0 size-[317px]"
                    >
                        <TestimonialsGlow className="absolute inset-[-158%] max-w-none size-full" />
                    </div>
                    <div className="relative flex gap-5 overflow-x-auto px-6 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {TESTIMONIALS_NEW.map((item, index) => (
                            <TestimonialCard key={`m-${item.name}-${index}`} item={item} />
                        ))}
                    </div>
                </div>

                {/* Desktop vertical marquees */}
                <div className="relative hidden min-h-[698px] min-w-0 flex-1 md:block">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute left-1/2 top-1/2 size-[420px] -translate-x-1/2 -translate-y-1/2"
                    >
                        <TestimonialsGlow className="absolute inset-[-160%] max-w-none size-full" />
                    </div>

                    <div className="relative flex h-[698px] justify-center gap-5 overflow-hidden [mask-image:linear-gradient(180deg,transparent_0%,#000_12%,#000_88%,transparent_100%)] [-webkit-mask-image:linear-gradient(180deg,transparent_0%,#000_12%,#000_88%,transparent_100%)]">
                        <MarqueeColumn items={colA} direction="up" offsetClass="pt-16" />
                        <MarqueeColumn items={colB} direction="down" />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsNew;
